import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PhevCalculator from './components/PhevCalculator';
import './App.scss';

const App: React.FC = () => {
  const { i18n } = useTranslation();

  // ── iframe auto-resize broadcaster ──────────────────────────────────────
  // Sends content height to parent with debouncing to avoid resize loops.
  useEffect(() => {
    if (window.parent === window) return;

    const root = document.getElementById('root');
    const target = root ?? document.body;
    let lastHeight = -1;
    let lastWidth = window.innerWidth;
    let frameId = 0;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let settledTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let observer: MutationObserver | null = null;
    let pollId: number | null = null;
    const lateTimeoutIds: Array<ReturnType<typeof setTimeout>> = [];

    // Measure only the root element. It is `height: auto`, so its height is
    // purely content-driven and never depends on the iframe's own dimensions
    // (viewport height or document scrollbars). Including doc/body sizes here
    // made the measurement oscillate with the iframe height set by the parent,
    // which fed back into the resize logic.
    const getContentHeight = () => {
      return Math.ceil(
        Math.max(
          target.getBoundingClientRect().height,
          target.scrollHeight
        )
      );
    };

    const sendHeight = () => {
      const height = getContentHeight()-1; // Substract 1px to prevent the white background
      if (Math.abs(height - lastHeight) < 2) return;
      lastHeight = height;

      window.parent.postMessage(
        { type: 'phev-calc-resize', height },
        '*'
      );
    };

    const scheduleSendHeight = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(() => {
        frameId = window.requestAnimationFrame(sendHeight);
      }, 80);
    };

    // Content-driven changes (collapsible toggles, lang switches) don't fire a
    // window resize. Wait out the CSS transition (500ms) so we only ever send
    // the final settled height, never intermediate frames.
    const scheduleSettledHeight = () => {
      if (settledTimeoutId) {
        clearTimeout(settledTimeoutId);
      }

      settledTimeoutId = setTimeout(() => {
        frameId = window.requestAnimationFrame(sendHeight);
      }, 620);
    };

    // The parent resizing this iframe (as a result of our own height messages)
    // fires a 'resize' event in here too, but it only changes the height, not
    // the width. Ignoring height-only resizes breaks the feedback loop:
    //   send -> parent resizes iframe -> resize fires -> re-send -> ...
    const handleWindowResize = () => {
      if (window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;
      scheduleSendHeight();
    };

    window.addEventListener('resize', handleWindowResize);
    window.addEventListener('orientationchange', scheduleSendHeight);

    // React content changes (slider moves, collapsible toggle, lang switch).
    if ('MutationObserver' in window) {
      observer = new MutationObserver(scheduleSettledHeight);
      observer.observe(target, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true,
      });
    }

    // Safety net: periodically re-measure and converge on the settled height,
    // so the iframe updates even if an event is missed. It only reports after
    // two consecutive identical measurements, so it never sends mid-transition
    // frames, and the lastHeight guard prevents repeat messages.
    let lastPolledHeight = -1;
    let stablePolls = 0;
    pollId = window.setInterval(() => {
      const h = getContentHeight();
      if (h === lastPolledHeight) {
        stablePolls += 1;
        if (stablePolls >= 2) {
          sendHeight();
        }
      } else {
        stablePolls = 0;
      }
      lastPolledHeight = h;
    }, 250);

    // Fire a couple of initial measurements to capture late style/font loads.
    scheduleSendHeight();
    lateTimeoutIds.push(setTimeout(scheduleSendHeight, 250));
    lateTimeoutIds.push(setTimeout(scheduleSendHeight, 800));

    return () => {
      window.removeEventListener('resize', handleWindowResize);
      window.removeEventListener('orientationchange', scheduleSendHeight);

      if (observer) {
        observer.disconnect();
      }

      if (pollId) {
        window.clearInterval(pollId);
      }

      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      if (settledTimeoutId) {
        clearTimeout(settledTimeoutId);
      }

      lateTimeoutIds.forEach((id) => clearTimeout(id));

      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [i18n.language]);

  return (
    <div className="app">
      <div className="app__lang-switcher">
        <button
          className={`app__lang-btn${i18n.language === 'en' ? ' app__lang-btn--active' : ''}`}
          type="button"
          aria-pressed={i18n.language === 'en'}
          onClick={() => i18n.changeLanguage('en')}
        >
          EN
        </button>
        <button
          className={`app__lang-btn${i18n.language === 'fr' ? ' app__lang-btn--active' : ''}`}
          type="button"
          aria-pressed={i18n.language === 'fr'}
          onClick={() => i18n.changeLanguage('fr')}
        >
          FR
        </button>
      </div>
      <PhevCalculator />
    </div>
  );
};

export default App;
